import { useState } from "react";
import { headlineFeed } from "@/mocks/expose";

type Filter = "all" | "hot" | "newest";
const tabs: { key: Filter; label: string }[] = [
  { key: "all", label: "最新跟进" },
  { key: "hot", label: "高热实锤" },
  { key: "newest", label: "按争议度" },
];

const tagColor: Record<string, string> = {
  实锤新增: "bg-primary-100 text-primary-700",
  私下聊天: "bg-accent-100 text-accent-800",
  未公开信息: "bg-secondary-100 text-secondary-900",
};

export default function HeadlineFeed() {
  const [filter, setFilter] = useState<Filter>("all");
  const [liked, setLiked] = useState<Record<number, boolean>>({});

  const list = filter === "hot" ? headlineFeed.filter((h) => h.hot) : headlineFeed;

  const toggle = (idx: number) => {
    setLiked((s) => ({ ...s, [idx]: !s[idx] }));
  };

  return (
    <section id="feed" className="mx-auto max-w-page px-4 py-12 md:px-6 md:py-16">
      <div className="flex flex-wrap items-end justify-between gap-4 border-b-2 border-foreground-950 pb-4">
        <div>
          <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-primary-600">
            <i className="ri-arrow-down-double-line text-base"></i>
            Headlines · 头条深挖
          </p>
          <h2 className="mt-2 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
            正在跟进的事件进展
          </h2>
        </div>
        <div className="flex rounded-full border border-background-200 bg-background-100 p-1">
          {tabs.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setFilter(t.key)}
              className={`whitespace-nowrap rounded-full px-4 py-1.5 text-xs transition-colors md:text-sm ${
                filter === t.key
                  ? "bg-primary-500 font-medium text-background-50"
                  : "text-foreground-600 hover:text-foreground-950"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* 高密度头条列表（上疏下密的下部密度来源） */}
      <div className="grid gap-6 lg:grid-cols-5">
        <div className="lg:col-span-3">
          <ol className="divide-y divide-background-200/70">
            {list.map((item, idx) => {
              const realIdx = headlineFeed.indexOf(item);
              const isLiked = !!liked[realIdx];
              const color = tagColor[item.tag] ?? "bg-background-100 text-foreground-600";
              return (
                <li key={item.title} className="group py-5">
                  <div className="flex flex-col gap-2 md:flex-row md:items-start md:gap-4">
                    <div className="flex items-center gap-2 md:w-28 md:shrink-0 md:pt-0.5">
                      <span className={`rounded px-2 py-0.5 text-[11px] font-medium ${color}`}>
                        {item.tag}
                      </span>
                      {item.hot && (
                        <span className="rounded bg-accent-500 px-1.5 py-0.5 text-[10px] font-bold text-background-50">
                          爆
                        </span>
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <h3 className="font-heading text-base font-semibold leading-7 text-foreground-900 group-hover:text-primary-700 md:text-lg">
                        {item.title}
                      </h3>
                      <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-foreground-500">
                        <span>{item.time}</span>
                        <span className="inline-flex items-center gap-1">
                          <i className="ri-chat-3-line"></i>
                          {item.comments} 讨论
                        </span>
                        <button
                          type="button"
                          onClick={() => toggle(realIdx)}
                          className={`inline-flex items-center gap-1 transition-colors ${
                            isLiked ? "text-primary-600" : "hover:text-primary-600"
                          }`}
                        >
                          <i className={isLiked ? "ri-heart-fill" : "ri-heart-line"}></i>
                          {isLiked ? "已标记" : "标记线索"}
                        </button>
                      </div>
                    </div>
                  </div>
                </li>
              );
            })}
          </ol>
        </div>

        {/* 深挖队列侧栏 */}
        <aside className="lg:col-span-2">
          <div className="rounded-lg bg-background-100 p-5">
            <h3 className="flex items-center gap-2 text-sm font-semibold text-foreground-900">
              <i className="ri-timer-flash-line text-accent-600"></i>
              深挖队列 · 正在收口
            </h3>
            <ul className="mt-3 space-y-3">
              {headlineFeed.slice(0, 4).map((item) => (
                <li key={`q-${item.title}`} className="flex items-start gap-3 rounded-md bg-background-50 p-3">
                  <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-accent-500"></span>
                  <div>
                    <p className="line-clamp-2 text-sm text-foreground-800">{item.title}</p>
                    <p className="mt-1 text-[11px] text-foreground-400">
                      待验证 {item.tag} · 编辑排期 2 天内更新
                    </p>
                  </div>
                </li>
              ))}
            </ul>
            <a
              href="#cta"
              className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-primary-700 hover:text-primary-600"
            >
              补充你掌握的线索 <i className="ri-arrow-right-line"></i>
            </a>
          </div>
        </aside>
      </div>
    </section>
  );
}